<script setup lang="ts">
import { ALAvatar, ALDivider } from '@af-lib/vue-ui';
import { Menu } from '@ark-ui/vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { CDN_ASSETS_BASE, fetchPersonality } from '@/services/cdn';
import { useUserStore } from '@/stores/user';

const t = inject<(key: string, options?: Record<string, unknown>) => string>('t')!;
const router = useRouter();
const userStore = useUserStore();

const FALLBACK_GRADIENTS = ['#F2F4F7', '#E6EAF0'];

const gradients = ref<string[]>(FALLBACK_GRADIENTS);

const user = computed(() => userStore.user);
const fullName = computed(() => [user.value?.first_name, user.value?.last_name].filter(Boolean).join(' '));

// Same path as phoenix's getAvatarPath / personal-account's $avatar helper.
const avatarUrl = computed(() => {
  const id = user.value?.branding?.id;
  return id ? `${CDN_ASSETS_BASE}/users/avatar/thumb/${id}.jpg` : '';
});

onMounted(async () => {
  try {
    await userStore.fetch();
  } catch {
    return; // unauthenticated — the router guard handles the redirect
  }

  const discId = user.value?.disc_id?.trim();
  if (!discId) return;

  try {
    const personality = await fetchPersonality();
    const g = personality[discId]?.gradients;
    if (g?.length === 2) gradients.value = g;
  } catch {
    /* non-blocking — keep the fallback gradient */
  }
});
</script>

<template>
  <Menu.Root v-if="user" :positioning="{ placement: 'bottom-end', offset: { mainAxis: 9 } }">
    <Menu.Trigger asChild>
      <button
        type="button"
        class="flex cursor-pointer items-center outline-none"
        :aria-label="t('accessibility.menu.general')"
        data-cy="navbar-user-menu"
      >
        <ALAvatar
          :firstName="user.first_name"
          :lastName="user.last_name"
          :avatarUrl="avatarUrl"
          :gradients="gradients"
          size="m"
          variant="square"
        />
      </button>
    </Menu.Trigger>

    <Teleport to="body">
      <Menu.Positioner class="z-1000!">
        <Menu.Content
          class="w-[295px] bg-af-white outline-none filter-(--shadow-lg)"
          data-cy="navbar-user-menu-content"
        >
          <div class="flex items-center gap-2 p-4">
            <ALAvatar
              :firstName="user.first_name"
              :lastName="user.last_name"
              :avatarUrl="avatarUrl"
              :gradients="gradients"
              size="l"
              variant="square"
            />
            <div class="flex min-w-0 flex-col">
              <span class="truncate text-sm font-medium text-af-blue-600">{{ fullName }}</span>
              <span class="truncate text-xs font-light italic text-af-grey-700">{{ user.email }}</span>
            </div>
          </div>

          <ALDivider type="horizontal" />

          <Menu.Item value="help" asChild>
            <a
              :href="t('general.support.url')"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="t('accessibility.link.help')"
              class="flex h-12 cursor-pointer items-center gap-3 px-4 text-sm font-light outline-none hover:bg-af-grey-100 data-[highlighted]:bg-af-grey-100"
            >
              <FaIcon :icon="['fat', 'circle-question']" class="h-4 w-4" />
              {{ t('navigation.help') }}
            </a>
          </Menu.Item>

          <ALDivider type="horizontal" />

          <Menu.Item
            value="logout"
            class="flex h-12 cursor-pointer items-center gap-3 px-4 text-sm font-light outline-none hover:bg-af-grey-100 data-[highlighted]:bg-af-grey-100"
            data-cy="navbar-logout"
            @select="router.push({ name: 'logout' })"
          >
            <FaIcon :icon="['fat', 'arrow-right-from-bracket']" class="h-4 w-4" />
            {{ t('navigation.logout') }}
          </Menu.Item>
        </Menu.Content>
      </Menu.Positioner>
    </Teleport>
  </Menu.Root>
</template>
